import { Box, ColorOptions, ModalDialog, Spacer, Text } from '@jfs/ui-new'
import { useFocusEffect } from '@react-navigation/native'
import React, { useCallback, useEffect, useRef, useState } from 'react'
import { ExpandAddView } from '../../components/ExpandAddView'
import { ListNew } from '../../components/ListNew'
import { ModalAlertView } from '../../components/ModalAlertView'
import { useLazyApiData, useStores } from '../../data/store'
import { isAccessible } from '../../utils'
import { ROUTING_PACKAGE_FILETYPE, getSchemaData } from '../../utils/constants'
import { EditPackage } from './EditPackage'
import { PackageForm } from './PackageForm'

const Package = () => {
  const [isModal, setModal] = useState(false)
  const [isExpand, setIsExpand] = useState(false)
  const [isDeleteModalVisible, setIsDeleteModalVisible] = useState(false)
  const [data, setData] = useState<any>({})

  const { getAll, accessConfig, update, deletePackage, config, fetchCodeNames, exportFileAsync } = useStores(
    (store) => ({
      getAll: store.package.getAll,
      length: store.package.packages.length,
      update: store.package.update,
      deletePackage: store.package.deletePackage,
      config: store.user.accessConfig,
      fetchCodeNames: store.user.fetchCodeNames,
      accessConfig: store.user.getAccessConfig(['routing', 'package']),
      exportFileAsync: store.exportFileAsync,
    }),
  )

  const [lazyGetAll, { state, data: apiData }] = useLazyApiData(getAll)
  const [lazyUpdate] = useLazyApiData(update)
  const [lazyFetchCodeNames, lazyFetchCodeNamesHandler] = useLazyApiData(fetchCodeNames)
  const [lazyDeletePackage] = useLazyApiData(deletePackage)
  const [lazyExportFileAsync, exportFileAsyncHandler] = useLazyApiData(exportFileAsync)

  const searchFormRef = React.useRef(null)

  useEffect(() => {
    if (isAccessible(accessConfig, 'add')) {
      lazyFetchCodeNames('PACKAGE_CATEGORY')
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lazyFetchCodeNames, !!accessConfig])

  useFocusEffect(
    useCallback(() => {
      if (isAccessible(accessConfig, 'fetch')) {
        lazyGetAll(searchFormRef?.current?.getValues() ?? {}, { page: 1, size: 10 })
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [lazyGetAll, config]),
  )

  const keyExtractor = (item: any): string => {
    return item.id
  }

  const headTitles: any = [
    { id: 'Package ID' },
    { group: 'Group Name' },
    { category: 'Category' },
    { packageName: 'Package Name' },
  ]
  isAccessible(accessConfig, 'update') && headTitles.push({ edit: '' })
  isAccessible(accessConfig, 'delete') && headTitles.push({ delete: '' })

  const sizeConfig: any = [1.5, 3, 3, 3]
  isAccessible(accessConfig, 'update') && sizeConfig.push(2)
  isAccessible(accessConfig, 'delete') && sizeConfig.push(2)
  const onEdit = (item: any) => {
    setData(item)
    setModal(true)
  }

  const onDelete = (item: any) => {
    setData(item)
    setIsDeleteModalVisible(true)
  }

  const abortControllerRef = useRef<AbortController>()
  const getAbortController = () => {
    abortControllerRef.current = new AbortController()
    return abortControllerRef.current
  }
  const exportFile = () => {
    lazyExportFileAsync(
      { fileType: 0, ...(searchFormRef?.current?.getValues() ?? {}) },
      'PACKAGE_NAME_LIST',
      undefined,
      getAbortController,
    )
  }

  const getMappingList = () => {
    return (
      <Box>
        <ListNew
          searchFormRef={searchFormRef}
          keyExtractor={keyExtractor}
          headTitle={headTitles}
          sizeConfig={sizeConfig}
          data={apiData?.content}
          onEdit={onEdit}
          onDelete={onDelete}
          listTitle={'Package Name List'}
          searchData={SEARCH_FILTERS}
          fetchDataFromServer={fetchDataFromServer}
          isSearchButtonLoading={state === 'pending'}
          apiHandler={{
            data: apiData,
            state,
          }}
          onRowItemClick={undefined}
          updatedPaginationInfo={{
            ...(apiData?.pageNo !== undefined && { page: apiData?.pageNo }),
            ...(apiData?.totalElements !== undefined && {
              total: apiData?.totalElements,
            }),
          }}
          exportFile={exportFile}
          isExportButtonLoading={exportFileAsyncHandler.state === 'pending'}
        />
      </Box>
    )
  }

  const onAdd = () => {
    setData({})
    setModal(true)
  }

  const onSuccess = () => {
    setIsExpand(false)
    setModal(false)
  }

  const handleUpdate = (value: any) => {
    lazyUpdate({
      id: data?.item?.id,
      group: data?.item?.group,
      category: value,
      fileType: ROUTING_PACKAGE_FILETYPE,
    })
    setModal(false)
  }

  const handleDelete = () => {
    lazyDeletePackage(data?.item)
    setIsDeleteModalVisible(false)
  }

  const fetchDataFromServer = useCallback(
    (data, paginationInfo) => {
      lazyGetAll(data, paginationInfo)
    },
    [lazyGetAll],
  )

  const renderModal = () => {
    if (!isModal) {
      return <></>
    }
    return (
      <EditPackage
        packageName={data?.item?.category}
        update={handleUpdate}
        deletePackage={handleDelete}
        close={() => setModal(false)}
      />
    )
  }
  return (
    <Box flex={1}>
      <Text title={'Package Creation Information'} fontSize={28} fontWeight={'600'} color={'#5a5c69' as ColorOptions} />
      <Spacer direction="vertical" size={24} />
      {isAccessible(accessConfig, 'add') && (
        <ExpandAddView
          children={
            <PackageForm
              data={data}
              packageCategoryList={getSchemaData(lazyFetchCodeNamesHandler?.data ?? [], 'PACKAGE_CATEGORY')}
              onSuccess={onSuccess}
              isDeletable={isAccessible(accessConfig, 'delete')}
              isEditable={isAccessible(accessConfig, 'update')}
            />
          }
          isExpand={isExpand}
          setIsExpand={setIsExpand}
          title={'Create Package'}
        />
      )}
      <Spacer direction="vertical" size={16} />
      {isAccessible(accessConfig, 'fetch') && getMappingList()}

      <ModalDialog show={isModal} children={renderModal()} type={'Centered'} />
      <ModalAlertView
        show={isDeleteModalVisible}
        onDismiss={() => setIsDeleteModalVisible(false)}
        title="Delete Rule Package"
        message={`Is this final. This will raise request in approvals to delete routing package with package name ${data?.item?.packageName}`}
        onPress={handleDelete}
        onCancel={() => setIsDeleteModalVisible(false)}
        buttonTitle="Delete"
      />
    </Box>
  )
}

export default Package

const SEARCH_FILTERS = [
  [
    { key: 'id', type: 'string', placeHolder: 'ID' },
    { key: 'group', type: 'string', placeHolder: 'Group' },
    { key: 'category', type: 'string', placeHolder: 'Category' },
    { key: 'packageName', type: 'string', placeHolder: 'Package Name' },
  ],
]
